import { types } from "mobx-state-tree"
import { endpoints } from "../api/ApiEndpoints"
import { api } from "../api/ApiService"
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage"
import { Column, ColumnType, DisplayValues } from "../../components/datatable/interfaces"

export enum ActivityTypes {
    VideoAnalysis = 1,
    HighlightDetection = 2,
    VideoConversion = 3,
    VideoOcr = 4,
    VideoAnalysisAndSegmentation = 5,
    VideoOcrAndAnalysis = 6,
    VideoOcrAndSegmentation = 7,
    Restore = 8,
    VideoOcrAnalysisAndSegmentation = 9
}

// How a charged activity_logs row was priced (mirrors the C# ActivityPriceType).
export enum ActivityPriceType {
    Original = 0,
    Discounted = 1
}

export function getActivityLabel(activity: ActivityTypes[]) {
    // The three-way is its own combination on the server (VideoOcrAnalysisAndSegmentation, one
    // priced row), so it gets its own name here too. It has to be tested before the pairs
    // below, each of which it would otherwise match — and did, printing a name that silently
    // dropped one of the three services the user paid for.
    if (activity.includes(ActivityTypes.VideoOcr) && activity.includes(ActivityTypes.VideoAnalysis) && activity.includes(ActivityTypes.HighlightDetection)) return "general.analysis-inspection-insights-and-segmentation";
    if (activity.includes(ActivityTypes.VideoAnalysis) && activity.includes(ActivityTypes.VideoOcr)) return "general.analysis-and-inspection-insights";
    if (activity.includes(ActivityTypes.VideoAnalysis) && activity.includes(ActivityTypes.HighlightDetection)) return "general.analysis-and-segmentation";
    // OCR + Segmentation (no Analysis) has its own name. It used to be collapsed into plain
    // "Inspection Insights", which made a real combination invisible: a file carrying OCR AND
    // masks read exactly like an OCR-only one, and "Inspection Insights & Segmentation" could
    // never be printed at all because no such label existed. Still must come before the
    // single-Segmentation check below, which would otherwise win.
    if (activity.includes(ActivityTypes.VideoOcr) && activity.includes(ActivityTypes.HighlightDetection)) return "general.inspection-insights-and-segmentation";
    if (activity.filter(a => a === ActivityTypes.VideoConversion).length === 1) return "general.video-conversion";
    if (activity.filter(a => a === ActivityTypes.VideoAnalysis).length === 1) return "general.video-analysis";
    if (activity.filter(a => a === ActivityTypes.HighlightDetection).length === 1) return "general.video-segmentation";
    if (activity.filter(a => a === ActivityTypes.VideoOcr).length === 1) return "general.inspection-insights";
    return ""
}

/**
 * The single activity type that stands for a set of services.
 *
 * The rules are ordered widest-first, deliberately: the order IS the content of this function.
 * A three-way run (OCR + Analysis + Segmentation) matches more than one rule, so whichever is
 * tested first decides the answer -- and three places used to disagree about it. This returned
 * VideoAnalysisAndSegmentation, getActivityLabel() above printed "Analysis & Inspection Insights",
 * and the server billed VideoOcrAndAnalysis. The visible consequences were a usage row whose
 * text and sort value named different apps, and a price preview (getPrices -> getPrice) quoting
 * a different bundle from the one actually charged. The three-way now has a type of its own on
 * both sides, so it is answered by the first rule instead of borrowing another combination's.
 *
 * Now it matches BundleTypeFor exactly. Keep the two in step: they answer the same question.
 */
export function getAppActivity(activities: ActivityTypes[]) {
    if (activities.includes(ActivityTypes.VideoOcr) && activities.includes(ActivityTypes.VideoAnalysis) && activities.includes(ActivityTypes.HighlightDetection)) return ActivityTypes.VideoOcrAnalysisAndSegmentation;
    if (activities.includes(ActivityTypes.VideoOcr) && activities.includes(ActivityTypes.VideoAnalysis)) return ActivityTypes.VideoOcrAndAnalysis;
    if (activities.includes(ActivityTypes.VideoAnalysis) && activities.includes(ActivityTypes.HighlightDetection)) return ActivityTypes.VideoAnalysisAndSegmentation;
    if (activities.includes(ActivityTypes.VideoOcr) && activities.includes(ActivityTypes.HighlightDetection)) return ActivityTypes.VideoOcrAndSegmentation;
    return activities[0];
}

export interface ActivityPrices {
    id: ActivityTypes;
    service: string;
    /** List price in euro-cents (1500 = 15 €); the field name predates the switch from credits. */
    credits: number;
    coverage: number;
}

export const emptyActivityPrices: ActivityPrices = {
    id: 1,
    service: "",
    credits: 0,
    coverage: 0
}

const columns: Column[] = [
    {
        key: "service",
        name: "shop.activity-prices.app",
        type: ColumnType.String
    },
    {
        key: "credits",
        name: "shop.activity-prices.credits",
        type: ColumnType.Number
    },
    {
        key: "coverage",
        name: "shop.activity-prices.coverage",
        type: ColumnType.Number
    }
]

const ActivityPricesModel = types.model({
    // Seed empty (not [emptyActivityPrices]) so `isLoading` stays true until the REAL
    // prices arrive — the boot gate then waits for prices instead of flashing 0s.
    prices: types.optional(types.frozen<ActivityPrices[]>(), [])
})

export const ActivityPricesStorageModel = types
    .compose(ActivityPricesModel, DataGridStorageModel)
    .named("ActivityPricesStorageModel")
    .actions((self) => {
        return {
            mapResponse(response: ActivityPrices[]) {
                self.prices = response;
                self.gridColumns = columns;
                self.data = {
                    totalCount: response.length,
                    items: response.map(self.mapItem)
                }
            },
            mapItem(item: ActivityPrices) {
                var displayValues : DisplayValues = {
                    id: {value: item.id, toDisplay: item.id},
                    service: {value: item.service, toDisplay: item.service},
                    credits: {value: item.credits, toDisplay: item.credits},
                    coverage: {value: 0, toDisplay: 0}
                }
                return {
                    displayValues: displayValues,
                    id: item.id
                }
            },
            getPrice(service:ActivityTypes) {
                return self.prices.find(p => p.id === service)?.credits || 0;
            },
            getPrices(services: ActivityTypes[]) {
                if (services.length === 1) return this.getPrice(services[0]);
                else return this.getPrice(getAppActivity(services));
            },
            updateCoverage(coins: number) {
                if (self.data === undefined) return;
                self.data.items.forEach((item: any, index: number) => {
                    var suffix = index < self.data.items.length - 1 ? " .. OR" : "";
                    // Guard against per-run price = 0 / null — `coins / 0 = Infinity`
                    // would render as "Infinity .. OR". A free activity covers infinite
                    // runs per credit, so show "∞" instead.
                    var perRun = self.data.items[index].displayValues.credits.value;
                    var coverage = (!perRun || perRun <= 0)
                        ? "∞" + suffix
                        : Math.floor(coins / perRun) + suffix;
                    self.data.items[index].displayValues.coverage.value = coverage;
                    self.data.items[index].displayValues.coverage.toDisplay = coverage;
                })
                self.setData({...self.data});
            },
            setItem(item: ActivityPrices) {
                var index = self.data.items.findIndex((i: any) => i.id === item.id);
                self.data.items[index].displayValues = {
                    id: {value: item.id, toDisplay: item.id},
                    service: {value: item.service, toDisplay: item.service},
                    credits: {value: item.credits, toDisplay: item.credits},
                    coverage: {value: item.coverage, toDisplay: item.coverage}
                }
            }
        }
    })
    .views((self) => ({
        get endpoint() {
            return endpoints.get("get_activity_prices");
        },
        get isLoading() {
            // loadAttempted: a finished load that returned no prices is a terminal state, not
            // "still loading" — otherwise this gates the App boot splash forever.
            if (self.error || self.loadAttempted) return false;
            return self.prices.length === 0;
        },
        get analysisPrice() {
            return self.prices.find(p => p.id === ActivityTypes.VideoAnalysis)?.credits || 0;
        },
        get conversionPrice() {
            return self.prices.find(p => p.id === ActivityTypes.VideoConversion)?.credits || 0;
        }
    }))

// Prices are a single global list (backend [AllowAnonymous], no per-org pricing), so
// autoload for everyone via the global subscription key — no organization key needed.
export const activityPricesStorage = ActivityPricesStorageModel.create({noPagination: true, showTotalAmount: false}, { api });
